import React, { FC, useRef, useState } from "react";

import { BaseComponentProps, useCurrentUser } from "@keepeek/commons";
import { KButton } from "@keepeek/refront-components";
import { Grow, Popper, Skeleton, Typography } from "@mui/material";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import { KIcon } from "../../components/common/KIcon";
import { KHeaderWidgetComponentContext } from "../widgets/KHeaderWidget/models";
import CurrentUserPopup from "./CurrentUserPopup";
import StyledCurrentUserMenu, { classes } from "./index.styles";

export type CurrentUserMenuProps = {
  showUserName?: boolean;
  color?: string;
  cookieConsentManagement: boolean;
} & BaseComponentProps;

const CurrentUserMenu: FC<React.PropsWithChildren<CurrentUserMenuProps>> = function ({
  showUserName = true,
  color,
  testId = "CurrentUserMenu",
  id,
  className,
  cookieConsentManagement,
}) {
  const { t } = useTranslation();
  const { currentUser, loadingUser } = useCurrentUser();

  const [open, setOpen] = useState<boolean>(false);
  const anchorRef = useRef(null);
  const menuId = "currentUserMenu";

  return (
    <StyledCurrentUserMenu cookieConsentManagement={cookieConsentManagement}>
      <KButton
        aria-label={t("layout.user-popup.aria") as string}
        className={clsx(classes.button, "CurrentUserMenu", className)}
        testId={testId}
        ref={anchorRef}
        aria-haspopup="true"
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((prev) => !prev)}
        id={id}
      >
        <KIcon
          fontSize="small"
          className={`${classes.accountIcon} fa-solid fa-circle-user fa-lg`}
          sx={{ color }}
        />
        {showUserName && currentUser && (
          <Typography
            variant="button"
            className={clsx(classes.buttonTitle, classes.accountTitle)}
            style={{ color }}
          >
            {`${currentUser?.firstName ?? ""} ${currentUser?.lastName ?? ""}`}
          </Typography>
        )}
        {showUserName && loadingUser && (!currentUser?.firstName || currentUser?.lastName) && (
          <Skeleton className={classes.usernameSkeleton} width={80} style={{ color }} />
        )}
        <KIcon sx={{ color }} className={"fa-solid fa-caret-down"} />
      </KButton>
      <Popper
        open={open}
        className={classes.popper}
        anchorEl={anchorRef.current}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <CurrentUserPopup
              onClose={() => setOpen(false)}
              open={open}
              context={KHeaderWidgetComponentContext.All}
              cookieConsentManagement={cookieConsentManagement}
            />
          </Grow>
        )}
      </Popper>
    </StyledCurrentUserMenu>
  );
};

export default CurrentUserMenu;
